import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RESPONSE_OPTIONS = [
  { value: 1, label: "Very Inaccurate", shortLabel: "Very Inaccurate" },
  { value: 2, label: "Moderately Inaccurate", shortLabel: "Mod. Inaccurate" },
  { value: 3, label: "Neither", shortLabel: "Neutral" },
  { value: 4, label: "Moderately Accurate", shortLabel: "Mod. Accurate" },
  { value: 5, label: "Very Accurate", shortLabel: "Very Accurate" },
];

export default function TestPage() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedInfo = sessionStorage.getItem("userInfo");
    if (!storedInfo) {
      toast.error("Please enter your information first");
      navigate("/");
      return;
    }
    setUserInfo(JSON.parse(storedInfo));
    fetchQuestions();
  }, [navigate]);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/questions`);
      const shuffled = [...response.data].sort(() => Math.random() - 0.5);
      setQuestions(shuffled);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch questions:", error);
      toast.error("Failed to load questions. Please try again.");
      setLoading(false);
    }
  };

  const handleAnswer = (value) => {
    const questionId = questions[currentIndex].id;
    setAnswers({ ...answers, [questionId]: value });

    if (currentIndex < questions.length - 1) {
      setTimeout(() => {
        setCurrentIndex(currentIndex + 1);
      }, 300);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (!answers[questions[currentIndex].id]) {
      toast.error("Please select an answer before continuing");
      return;
    }
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleSubmit = async () => {
    const unanswered = questions.filter((q) => !answers[q.id]);
    if (unanswered.length > 0) {
      toast.error(`Please answer all questions. ${unanswered.length} remaining.`);
      const firstUnansweredIndex = questions.findIndex((q) => !answers[q.id]);
      setCurrentIndex(firstUnansweredIndex);
      return;
    }

    setIsSubmitting(true);

    try {
      const submission = {
        user_info: userInfo,
        answers: Object.entries(answers).map(([qid, value]) => ({
          question_id: parseInt(qid),
          value: value,
        })),
      };

      const response = await axios.post(`${API}/test/submit`, submission);
      sessionStorage.removeItem("userInfo");
      toast.success("Assessment complete!");
      navigate(`/results/${response.data.id}`);
    } catch (error) {
      console.error("Failed to submit test:", error);
      toast.error("Failed to submit. Please try again.");
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-amber-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading questions...</p>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load</h2>
          <p className="text-gray-600 mb-6">Unable to load the assessment questions.</p>
          <Button onClick={() => window.location.reload()} className="bg-sky-500 hover:bg-sky-600">Try Again</Button>
        </Card>
      </div>
    );
  }

  const progress = ((Object.keys(answers).length) / questions.length) * 100;
  const currentQuestion = questions[currentIndex];
  const currentAnswer = answers[currentQuestion.id];
  const isLastQuestion = currentIndex === questions.length - 1;
  const allAnswered = Object.keys(answers).length === questions.length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/")}
                className="text-gray-600 hover:text-gray-900"
                data-testid="back-home-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Exit
              </Button>
              <img 
                src="https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/wzCO7KFXOfMKBfoO2dpF/media/6774787e91c39998b2e142f0.jpeg" 
                alt="IntegrityGO" 
                className="h-8 hidden md:block"
              />
            </div>
            <div className="text-center">
              <span className="text-sm font-medium text-gray-900">
                Question {currentIndex + 1} of {questions.length}
              </span>
            </div>
            <div className="w-20" />
          </div>
          <Progress value={progress} className="h-2 bg-gray-100" />
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12 max-w-3xl">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="p-8 md:p-12 bg-white shadow-sm border-gray-100">
              <div className="text-center mb-10">
                <span className="inline-block px-3 py-1 bg-sky-100 text-sky-800 text-sm font-medium rounded-full mb-6">
                  How accurately does this describe you?
                </span>
                <h2 
                  className="text-2xl md:text-3xl font-semibold text-gray-900 leading-relaxed"
                  data-testid="question-text"
                >
                  "{currentQuestion.text}"
                </h2>
              </div>

              <div className="space-y-3">
                {RESPONSE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer(option.value)}
                    data-testid={`answer-option-${option.value}`}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-200 flex items-center justify-between group
                      ${
                        currentAnswer === option.value
                          ? "border-sky-500 bg-sky-50"
                          : "border-gray-200 hover:border-gray-300 bg-white"
                      }
                    `}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors
                          ${
                            currentAnswer === option.value
                              ? "border-amber-500 bg-sky-500"
                              : "border-gray-300 group-hover:border-gray-400"
                          }
                        `}
                      >
                        {currentAnswer === option.value && (
                          <CheckCircle className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <span
                        className={`font-medium ${
                          currentAnswer === option.value
                            ? "text-gray-900"
                            : "text-gray-700"
                        }`}
                      >
                        <span className="hidden md:inline">{option.label}</span>
                        <span className="md:hidden">{option.shortLabel}</span>
                      </span>
                    </div>
                    <span
                      className={`text-sm ${
                        currentAnswer === option.value
                          ? "text-sky-600"
                          : "text-gray-400"
                      }`}
                    >
                      {option.value}
                    </span>
                  </button>
                ))}
              </div>
            </Card>

            <div className="flex items-center justify-between mt-8">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentIndex === 0}
                className="px-6"
                data-testid="prev-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>

              {isLastQuestion ? (
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !allAnswered}
                  className="px-8 bg-sky-500 hover:bg-sky-600"
                  data-testid="submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Assessment
                      <CheckCircle className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  disabled={!currentAnswer}
                  className="px-6 bg-sky-500 hover:bg-sky-600"
                  data-testid="next-btn"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>

            <div className="mt-8 text-center">
              <p className="text-sm text-gray-500">
                {Object.keys(answers).length} of {questions.length} questions answered
              </p>
              {!allAnswered && isLastQuestion && (
                <p className="text-sm text-sky-600 mt-2">
                  Please answer all questions before submitting
                </p>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}
